// procurementPlans.js — 발주 계획 앱(project-procurement-plan)의 purchasePlans 컬렉션을 읽기 전용으로 조회/변환하는 공용 모듈.
// PurchasePlanHistoryView(이력 화면)와 App.js(구매계획 자동 반영)가 함께 사용한다.
import { initializeApp, getApp, getApps } from "firebase/app";
import { getFirestore, collection, getDocs } from "firebase/firestore";

// 발주 계획 앱의 공개 Firebase 설정 (읽기 전용, purchasePlans 컬렉션은 비로그인 read 허용됨)
const PROCUREMENT_CONFIG = {
  apiKey: "AIzaSyCedlT3oxPzAjv4SfpMHuOZvvg1_8aQp2M",
  authDomain: "project-procurement-plan.firebaseapp.com",
  projectId: "project-procurement-plan",
  storageBucket: "project-procurement-plan.firebasestorage.app",
  messagingSenderId: "847666618864",
  appId: "1:847666618864:web:61d94acaaab61419f47d35",
};

function procurementDb() {
  const app = getApps().find((a) => a.name === "procurement")
    ? getApp("procurement")
    : initializeApp(PROCUREMENT_CONFIG, "procurement");
  return getFirestore(app);
}

// 외화(달러) 여부 판정. 실제 저장값은 "USD"가 아니라 통화 기호("$" / "₩")로 들어온다.
export const isUSD = (cur) => cur === "$" || String(cur || "").toUpperCase() === "USD";

// 발주 앱의 구매계획 이력 전체를 최신순으로 가져온다.
export async function fetchProcurementPlans() {
  const snap = await getDocs(collection(procurementDb(), "purchasePlans"));
  const list = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
  list.sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0)); // 최신순
  return list;
}

// purchasePlans 이력 → 재고 앱의 raw.plan(발주량/입고예정) + planDates(도착예정일 목록) 형태로 변환.
// ✅ 2026-08-27 실데이터 전수 확인 — purchasePlans 문서의 날짜 필드는 다음 다섯 개다.
//   issueDate  발행일
//   date       최종 선적일자 (결제조건 "45 days after shipment" 등의 기산일)
//   blDate     B/L 일자
//   etaDate    입항 예정일  ← 발주 앱에서 '입고정리'로 수정하는 날짜가 바로 이것
//   confirmedAt 확정일시
//
// ⚠️ 예전 코드는 입고 예정 스케줄을 date(선적일) 기준으로 만들고 있었다.
//   그래서 발주 앱에서 입항일을 고쳐도(etaDate) 재고 화면은 계속 선적일을 보여줬다.
//   예: Kingfa 70,500개 — date 2026-08-28(선적) / etaDate 2026-09-18(입항) → 3주 앞당겨 입고된 것처럼 표시됨.
//   → 2026-09-08: 발주 앱의 '납기'(=date)를 입고 기준으로 쓴다. 납기(date)를 우선 사용하고, date가 없는 발주만 입항 예정일(etaDate)로 대체한다.
//   결제 계산(paymentSchedule)은 선적일 기산이 맞으므로 계속 date를 쓴다 — 두 필드의 용도가 이제 분리됐다.
export const inboundDateOf = (p) => (p && (p.date || p.etaDate)) || "";
// - done(완료 체크)된 발주는 이미 입고 반영된 것으로 보고 제외한다(재고 업로드에 이미 반영되었을 것이므로 중복 집계 방지).
export function plansToRawPlan(plans) {
  const map = new Map();
  const datesSet = new Set();
  (plans || []).forEach((p) => {
    if (p.done) return;
    const date = inboundDateOf(p); // 납기일(date) 우선, 없으면 입항 예정일(etaDate)
    if (date) datesSet.add(date);
    (Array.isArray(p.rows) ? p.rows : []).forEach((r) => {
      const code = String(r.code || "").trim();
      if (!code) return;
      if (!map.has(code)) map.set(code, { code, acode: "", order: 0, inbound: {} });
      const it = map.get(code);
      if (!it.acode && r.memo) it.acode = String(r.memo).trim();
      const qty = Number(r.qty) || 0;
      it.order += qty;
      if (date) it.inbound[date] = (it.inbound[date] || 0) + qty;
    });
  });
  return { recs: Array.from(map.values()), dates: Array.from(datesSet).sort() };
}

// 이력 화면 상단 요약 카드용 집계.
export function summarizePlans(plans) {
  const list = plans || [];
  let totQty = 0, krwAmt = 0, usdAmt = 0, usdCount = 0, usdKrwConv = 0;
  list.forEach((p) => {
    totQty += Number(p.totQty) || 0;
    const amt = Number(p.totAmt) || 0;
    if (isUSD(p.cur)) {
      usdAmt += amt;
      usdCount += 1;
      usdKrwConv += amt * (Number(p.rate) || 0);
    } else {
      krwAmt += amt;
    }
  });
  return { count: list.length, totQty, krwAmt, usdAmt, usdCount, usdKrwConv, grand: krwAmt + usdKrwConv };
}

// 거래처별 매입 집계 (매출매입 화면의 '매입' 탭용). KRW 환산 금액 기준으로 내림차순 정렬.
// done(완료 체크)된 발주도 포함한다 — 매입은 실제 발생한 지출로 봐야 하므로 raw.plan(입고전망)과 달리 제외하지 않는다.
export function summarizeByVendorPurchase(plans) {
  const map = new Map();
  (plans || []).forEach((p) => {
    const vendor = p.vendor || "(거래처 미지정)";
    if (!map.has(vendor)) map.set(vendor, { vendor, count: 0, krw: 0, usd: 0, conv: 0, lastDate: "", items: [] });
    const v = map.get(vendor);
    const amt = Number(p.totAmt) || 0;
    v.count += 1;
    if (isUSD(p.cur)) { v.usd += amt; v.conv += amt * (Number(p.rate) || 0); }
    else { v.krw += amt; v.conv += amt; }
    const d = p.date || p.confirmedAt || "";
    if (d && d > v.lastDate) v.lastDate = d;
    v.items.push({ date: p.date, confirmedAt: p.confirmedAt, cur: p.cur, rate: p.rate, totAmt: p.totAmt, payTerm: p.payTerm, done: !!p.done });
  });
  return Array.from(map.values()).sort((a, b) => b.conv - a.conv);
}

function ym(dateStr) {
  const d = new Date(String(dateStr) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// payTerm(결제조건, 자유 텍스트)을 해석해 결제가 실제로 발생하는 월(들)을 산출한다.
// ⚠️ 2026-07-25: p.date(=due)는 "도착예정일"이 아니라 "최종 선적일자"다(사용자 확인).
// 규칙 (발주 계획 앱의 "월별 결제 예상" 화면과 동일):
//  - "선지급 N% + ..." → N%는 발행일(issueDate) 기준 월, 나머지는 최종 선적일(date) 기준 월
//  - "N일" / "N days" 표현 포함 → 최종 선적일 + N일이 속한 월 (예: 45일 → 선적일+45일)
//  - "익월말" 포함 → 발행일 기준 다음 달
//  - 그 외(즉시 결제 등) → 최종 선적일이 속한 월
// 거래처별 결제조건 덮어쓰기 — 발주 앱에 입력된 payTerm 텍스트와 실제 결제 관행이 다른 경우에 사용한다.
// 발주 앱 쪽 데이터를 고치지 않고 재고 앱에서 현금흐름만 실제에 맞게 보정하기 위한 장치다.
// (2026-07-25: 가디언케어는 발주 앱에 "월말결제"/"익월말결제"로 입력돼 있으나 실제로는 선적 후 10일 결제 — 사용자 확인)
// - daysAfterShipment: payTerm 텍스트를 무시하고 "선적일(date) + N일"에 결제
// - monthEndBasis:"due": "월말결제"류를 발행일이 아니라 납기(date=최종 선적일) 기준 월로 계산
const VENDOR_PAYTERM_OVERRIDES = [
  { match: /가디언케어/, daysAfterShipment: 10 },
  { match: /에스디씨|\bSDC\b/i, monthEndBasis: "due" }, // 2026-07-25: SDC는 납기 기준 월말결제 (사용자 확인)
];

export function paymentSchedule(p) {
  const term = String(p.payTerm || "");
  const issue = p.issueDate;
  const due = p.date;
  const amt = Number(p.totAmt) || 0;

  // 거래처 덮어쓰기 처리 (아래 일반 규칙보다 우선한다)
  const ov = VENDOR_PAYTERM_OVERRIDES.find((v) => v.match.test(p.vendor || ""));
  if (ov && due) {
    // ① 선적일 + N일 결제
    if (ov.daysAfterShipment != null) {
      const d = new Date(due + "T00:00:00");
      d.setDate(d.getDate() + ov.daysAfterShipment);
      return [{ month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, amount: amt }];
    }
    // ② "월말결제"류를 발행일이 아니라 납기(선적일) 기준 월로 계산
    if (ov.monthEndBasis === "due" && term.includes("월말")) {
      if (term.includes("익월말")) {
        const d = new Date(due + "T00:00:00");
        d.setMonth(d.getMonth() + 1);
        return [{ month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, amount: amt }];
      }
      return [{ month: ym(due), amount: amt }];
    }
  }

  const pct = term.match(/선지급\s*(\d+(?:\.\d+)?)\s*%/);
  if (pct && issue && due) {
    const r = Number(pct[1]) / 100;
    const out = [];
    const m1 = ym(issue), m2 = ym(due);
    if (m1) out.push({ month: m1, amount: amt * r });
    if (m2) out.push({ month: m2, amount: amt * (1 - r) });
    return out;
  }

  // ⚠️ 2026-07-25 수정: 기존엔 /(\d+)\s*(?:일|days?)\b/ 하나로 한글·영문을 같이 매칭했는데,
  // JS 정규식의 \b(단어 경계)는 한글을 "단어 문자"로 인식하지 않아서 "일" 바로 뒤에 다른 한글이 오면
  // (그리고 "45일"처럼 문자열이 "일"로 끝나도!) \b가 성립하지 않아 매칭 자체가 실패했다.
  // 그 결과 "45일", "45일후", "45일이내" 같은 가장 흔한 표기가 전부 인식되지 못하고 조용히
  // 기본값(도착예정일이 속한 달에 즉시 결제)으로 빠지고 있었다 — N일 지연이 통째로 반영 안 된 원인.
  const days = term.match(/(\d+)\s*일/) || term.match(/(\d+)\s*days?\b/i);
  if (days && due) {
    const d = new Date(due + "T00:00:00");
    d.setDate(d.getDate() + Number(days[1]));
    return [{ month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, amount: amt }];
  }

  if (term.includes("익월말") && issue) {
    const d = new Date(issue + "T00:00:00");
    d.setMonth(d.getMonth() + 1);
    return [{ month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, amount: amt }];
  }

  // ⚠️ 2026-07-25 추가: "월말결제"(익월말 아님, "이번 달 말일에 정산") — 발행일이 속한 달 기준.
  // 발주 계획 앱의 "월별 결제 예상" 화면과 대조해서 확인함(예: 발행일 7/21·"월말결제" → 7월 결제로 잡혀야
  // 하는데, 이 분기가 없으면 아래 기본값(선적일이 속한 달)으로 빠져 엉뚱한 달로 밀리는 문제가 있었다).
  if (term.includes("월말") && issue) {
    return [{ month: ym(issue), amount: amt }];
  }

  const m = ym(due);
  return m ? [{ month: m, amount: amt }] : [];
}

// 현재 연-월 (YYYY-MM)
export const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// '정산이 끝난 발주'인지 판정 — 완료(done) 처리됐고 마지막 결제월이 이번 달보다 이전이면 true.
// 구매계획 이력 목록에서 이런 건은 기본으로 감춘다(더 이상 지켜볼 일이 없는 과거 건).
// - 완료가 아니면 진행중이므로 항상 보인다.
// - 완료여도 결제월이 이번 달이거나 앞으로면(예: "45 days after shipment"로 결제가 남은 건) 보인다.
// - payTerm 해석이 안 돼 결제월을 못 구하면 판단 근거가 없으므로 감추지 않는다(놓치는 쪽보다 보이는 쪽이 안전).
export function isSettledPast(p, nowMonth) {
  if (!p || !p.done) return false;
  const months = paymentSchedule(p).map((s) => s.month).filter(Boolean);
  if (months.length === 0) return false;
  const last = months.slice().sort().slice(-1)[0];
  return last < (nowMonth || currentMonth());
}

// 월별 결제 예상 — 완료(done) 처리된 발주는 제외.
export function monthlyPaymentForecast(plans) {
  const map = new Map();
  (plans || []).forEach((p) => {
    if (p.done) return;
    paymentSchedule(p).forEach(({ month, amount }) => {
      if (!month) return;
      if (!map.has(month)) map.set(month, { month, krw: 0, usd: 0, conv: 0 });
      const row = map.get(month);
      if (isUSD(p.cur)) {
        row.usd += amount;
        row.conv += amount * (Number(p.rate) || 0);
      } else {
        row.krw += amount;
        row.conv += amount;
      }
    });
  });
  const months = Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  const total = months.reduce(
    (acc, m) => ({ krw: acc.krw + m.krw, usd: acc.usd + m.usd, conv: acc.conv + m.conv }),
    { krw: 0, usd: 0, conv: 0 }
  );
  return { months, total };
}
